import type { DataOption } from '@/common.types'
import type { SettingsTemplateModelType } from '@/entities/settings-template'

import React, { useCallback, useMemo } from 'react'
import { ArrowRightIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	findCatalogVariant,
	normalizeTacticParams,
	usePricingRuleCatalogQuery,
} from '@/entities/pricing-rule'

import { getSchemaIntertpretationsList } from '@/shared/components/SchemaInterpretator'
import { Separator } from '@/shared/components/ui/separator'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { SettingsTemplateType } from '@/shared/generated'
import { intlFormat } from '@/shared/tools/dates'
import { translateSchemaKeys } from '@/shared/tools/json-schema-utils'

import { useSettingsTemplateInterpretationProps } from '../lib/useSettingsTemplateInterpretationProps'

export function SettingsTemplateGenaratedShortDescription({
	template,
}: {
	template: SettingsTemplateModelType
}) {
	const { t } = useTranslation()

	const { schema, uiSchema, isLoading } =
		useSettingsTemplateInterpretationProps({ type: template.template_type })
	// The rule name comes from the catalog, not the settings schema: the schema
	// only describes system rules, and a template can pin a user rule version.
	const isRbTactics =
		template.template_type === SettingsTemplateType.PricingCampaignTacticsRb
	const { data: ruleCatalog } = usePricingRuleCatalogQuery({
		enabled: isRbTactics,
	})
	const ruleLabel = useMemo(() => {
		if (!isRbTactics) return undefined

		const params = normalizeTacticParams(
			(template.body as any)?.pricing_tactics?.params
		)
		const variant = findCatalogVariant(ruleCatalog?.data ?? [], params)

		if (!variant) return params.pricing_rule_id ?? undefined

		return variant.marked_name
			? `${variant.name} (${variant.marked_name})`
			: variant.name
	}, [isRbTactics, template.body, ruleCatalog?.data])
	const interpretation = useMemo(
		() =>
			getSchemaIntertpretationsList({
				schema: translateSchemaKeys(schema?.properties?.body || {}, t),
				data: template.body,
				uiSchema,
			}),
		[template.body, schema?.properties?.body]
	)
	const getFieldValue = useCallback(
		(name: string) => {
			const field = interpretation.find(({ key }) => key === name)
			if (field?.resolvedValue) {
				return Array.isArray(field?.resolvedValue)
					? field?.resolvedValue?.map(({ label }) => label).join(', ')
					: (field?.resolvedValue as DataOption)?.label
			} else {
				return field?.currentValue
			}
		},
		[interpretation]
	)
	if (isLoading) {
		return <Skeleton className='h-4 w-full' />
	}
	switch (template.template_type) {
		case SettingsTemplateType.PricingCampaignActivity:
			return (
				<div className='flex items-center gap-1 text-sm text-muted-foreground'>
					{template.body.activity.start_date &&
						intlFormat(new Date(template.body.activity.start_date))}
					<ArrowRightIcon className='h-4' />
					{template.body.activity.end_date &&
						intlFormat(new Date(template.body.activity.end_date))}
				</div>
			)
		case SettingsTemplateType.ScenarioStopList: {
			return (
				<div className='flex items-center gap-2 text-sm text-muted-foreground'>
					<span>
						{t('stop_list_custom_rules_title')}:{' '}
						{template.body.stop_list.custom_rules?.length}
					</span>
					<Separator orientation='vertical' className='h-4' />
					<span>
						{t('stop_list_default_rules_title')}:{' '}
						{
							Object.values(template.body.stop_list.default_rules).filter(
								Boolean
							)?.length
						}
					</span>
				</div>
			)
		}

		case SettingsTemplateType.ScenarioBusinessConstraints:
			return (
				<div className='flex items-center gap-2 text-sm text-muted-foreground'>
					<span>
						{t('og.settings.business')}:{' '}
						{Object.values(template.body.business).filter(Boolean)?.length}
					</span>
				</div>
			)
		case SettingsTemplateType.PricingCampaignLimits:
			return (
				<div className='flex items-center gap-2 text-sm text-muted-foreground'>
					<span>
						{t('price_limits_lower_title')}:{' '}
						{template.body.price_limits.lower?.length}
					</span>
					<Separator orientation='vertical' className='h-4' />
					<span>
						{t('price_limits_upper_title')}:{' '}
						{template.body.price_limits.upper?.length}
					</span>
					<Separator orientation='vertical' className='h-4' />
					<span>
						{t('price_limits_on_violation_title')}:{' '}
						{template.body.price_limits.extra.on_conflict}
					</span>
				</div>
			)
		case SettingsTemplateType.PricingCampaignRounding:
			return (
				<div className='flex items-center gap-2 text-sm text-muted-foreground'>
					<span>
						{t('rounding_rules_title')}: {template.body.rounding?.length}
					</span>
				</div>
			)
		case SettingsTemplateType.PricingCampaignMinimalSteps:
			return (
				<div className='flex items-center gap-2 text-sm text-muted-foreground'>
					<span>
						{t('minimal_steps', { defaultValue: 'Minimal steps' })}:{' '}
						{template.body.minimal_steps?.length}
					</span>
				</div>
			)
		case SettingsTemplateType.PricingCampaignTacticsMd:
			return (
				<div className='flex items-center gap-2 text-sm text-muted-foreground'>
					<span>
						{t('engine', { defaultValue: 'Engine' })}:{' '}
						{template.body.pricing_tactics.engine}
					</span>
					<Separator orientation='vertical' className='h-4' />
					<span>
						{t('markdown_periods')}:{' '}
						{template.body.pricing_tactics.params.periods?.length}
					</span>
				</div>
			)
		case SettingsTemplateType.PricingCampaignTacticsPromo:
			return (
				<div className='flex items-center gap-3 text-xs text-wrap text-muted-foreground'>
					{template.body.pricing_tactics.params.promo_start_date && (
						<span>
							{t('promo_period_start_date', {
								defaultValue: 'Promo period start date',
							})}
							: {template.body.pricing_tactics.params.promo_start_date}
						</span>
					)}
					<Separator orientation='vertical' className='h-4' />
					{template.body.pricing_tactics.params.promo_end_date && (
						<span>
							{t('promo_period_end_date', {
								defaultValue: 'Promo period end date',
							})}
							: {template.body.pricing_tactics.params.promo_end_date}
						</span>
					)}
					<Separator orientation='vertical' className='h-4' />
					<span>
						{t('promo_period_target', { defaultValue: 'Promo period target' })}:{' '}
						{t(
							`promo_pc.${template.body.pricing_tactics?.params?.target?.name}.title`,
							{
								defaultValue:
									template.body.pricing_tactics?.params?.target?.name,
							}
						)}{' '}
						{template.body.pricing_tactics?.params?.target?.value}
					</span>
				</div>
			)
		case SettingsTemplateType.PricingCampaign:
			return (
				<div className='flex items-center gap-2 text-sm text-muted-foreground'>
					<span>
						{t('engine', { defaultValue: 'Engine' })}:{' '}
						{template.body.settings?.pricing_tactics?.engine}
					</span>
				</div>
			)
		case SettingsTemplateType.Scenario:
			return (
				<div className='flex items-center gap-2 text-sm text-muted-foreground'>
					<span>
						{t('og_metric_to_grow')}:{' '}
						{getFieldValue('settings.strategy.demand_strategy.target')}
					</span>
					<Separator orientation='vertical' className='h-4' />
					<span>
						{t('og_metric_to_maintain')}:{' '}
						{getFieldValue('settings.strategy.demand_strategy.protect')}
					</span>
					<Separator orientation='vertical' className='h-4' />
					<span>
						{t('general_pc')}: {template.body.pricing_campaigns?.length}
					</span>
				</div>
			)
		case SettingsTemplateType.ScenarioStrategy:
			return (
				<div className='flex items-center gap-2 text-sm text-muted-foreground'>
					<span>
						{t('og_metric_to_grow')}:{' '}
						{getFieldValue('strategy.demand_strategy.target')}
					</span>
					<Separator orientation='vertical' className='h-4' />
					<span>
						{t('og_metric_to_maintain')}:{' '}
						{getFieldValue('strategy.demand_strategy.protect')}
					</span>
				</div>
			)
		case SettingsTemplateType.PricingCampaignAnchoring:
			return (
				<div className='flex items-center gap-2 text-sm text-muted-foreground'>
					<span>
						{t('rules', { defaultValue: 'Rules' })}:{' '}
						{template.body?.anchoring.rules?.length}
					</span>
				</div>
			)
		case SettingsTemplateType.PricingCampaignTacticsRb:
			return (
				<div className='flex items-center gap-2 text-sm text-muted-foreground'>
					<span>
						{t('rule', { defaultValue: 'Rule' })}: {ruleLabel ?? '—'}
					</span>
				</div>
			)
		case SettingsTemplateType.PricingCampaignProductAssignment:
			return (
				<div className='flex items-center gap-2 text-sm text-muted-foreground'>
					<span>
						{t('segments', { defaultValue: 'Segments' })}:{' '}
						{template.body?.product_assignments?.length}
					</span>
				</div>
			)
		default:
			return (
				<div className='flex items-center gap-2 text-sm text-muted-foreground'>
					<span>-</span>
				</div>
			)
	}
}
